/* eslint @typescript-eslint/no-explicit-any: "off" */
/* eslint @typescript-eslint/ban-ts-comment: "off" */

import {
    TreeNodeData,
    VisibleField,
    SortingOption,
    Filters,
} from "@tenforce/product-fields"

import {
    ConceptFieldsResult,
    ConceptFieldTree,
    GroupData,
    IItemsCollectionResult,
    SortingOptionTree,
    VisibleFieldResult,
} from "../../components/collection"
import { RequestErrorDataApi } from "../../errors"
import { formatString } from "../../util/Strings"
import { Exceptions } from "../../util/Translations"
import { dataAPI } from "../api"
import {
    ODataGetArrayResponse,
    ODataGetResourceResponse,
} from "../models/api/data"
import {
    CollectionItemViewDTO,
    ConceptFieldGroupDTO,
    SortingDTO,
    VisibleFieldResultDTO,
} from "../models/dto/collection"
import { parseFieldData } from "../utils/fieldparsing"
import { IUserFormattingSettings } from "../../components/applicationheader/components/usermenu"
import { IField } from "../../components/fields"
import { FieldDefinitionDTO } from "../models/treeSelector/models"
import { parseFromDTO, parseToDTO } from "../utils/conditions"
import { typeHasGroups } from "../treeSelector/utils"
import { FieldType } from "../../components/thumbtableview"
import {
    OptionFieldDefinition,
    TextFieldDefinition,
} from "../models/thumbtable"
import { IItem } from "../../models"
import { NumberCode, TitleCode } from "../utils/constants"
import {
    FieldDefinitionTypeToSelectableDTOTypeMap,
    getSelectableConditionType,
} from "../utils/conditions/SelectorConditionParser"

const {
    AnErrorOccurredWhileFetchingVisibleFieldsFor = "An error occurred while fetching the visible fields",
    AnErrorOccurredWhileFetchingAllConceptFields = "An error occurred while fetching the all concept fields",
    AnErrorOccurredWhileFetchingConceptFieldGroups = "An error occurred while fetching the concept groups",
} = Exceptions

export const parseFieldType = (component: FieldDefinitionDTO): FieldType => {
    const odataType = component["@type"]
    switch (odataType) {
        case "#TF.TextFieldDefinition": {
            const { TextFieldType } = component as TextFieldDefinition
            return TextFieldType as FieldType
        }
        case "#TF.TimeFieldDefinition":
            return "Time"
        case "#TF.NumberFieldDefinition":
            return "Number"
        case "#TF.BoolFieldDefinition":
            return "Checkbox"
        case "#TF.DateTimeOffsetFieldDefinition":
        case "#TF.DateFieldDefinition":
            return "Date"
        case "#TF.StatusFieldDefinition":
        case "#TF.PriorityFieldDefinition":
        case "#TF.UserFieldDefinition":
        case "#TF.UserGroupFieldDefinition":
        case "#TF.OrganizationUnitFieldDefinition":
        case "#TF.AccountCodeFieldDefinition":
        case "#TF.ActivityFieldDefinition":
        case "#TF.ApplicationFieldDefinition":
        case "#TF.ApplicationVersionFieldDefinition":
        case "#TF.BillTypeFieldDefinition":
        case "#TF.DisplayTypeFieldDefinition":
        case "#TF.ImpactFieldDefinition":
        case "#TF.ItemFieldDefinition":
        case "#TF.ProbabilityFieldDefinition":
            return "SingleSelect"
        case "#TF.OptionFieldDefinition": {
            const { OptionFieldType } = component as OptionFieldDefinition
            return OptionFieldType === "Toggle" ? "Toggle" : "SingleSelect"
        }
        case "#TF.UsersFieldDefinition":
        case "#TF.UserGroupsFieldDefinition":
        case "#TF.ItemsFieldDefinition":
        case "#TF.OptionsFieldDefinition":
            return "MultiSelect"
        case "#TF.TagsFieldDefinition":
            return "Tags"
        case "#TF.RtfConceptViewFieldComponent":
            return "RTF"
        case "#TF.AttachmentsConceptViewFieldComponent":
            return "Attachment"
        case "#TF.LocationFieldDefinition":
        case "#TF.ConceptFieldDefinition":
        case "#TF.AnswerFieldDefinition":
        case "#TF.ExplanatoryFieldDefinition":
            return "Text"
        case "#TF.SignatureFieldDefinition":
            return "Signature"
        default:
            return "Default"
    }
}

const parseVisibleFieldData = (dtoData: FieldDefinitionDTO): VisibleField => {
    return {
        id: dtoData.Id,
        label: dtoData.Label,
        type: parseFieldType(dtoData),
        oDataType: dtoData["@type"],
        isGrouped: typeHasGroups(
            getSelectableConditionType(
                dtoData[
                    "@type"
                ] as keyof typeof FieldDefinitionTypeToSelectableDTOTypeMap,
            ),
        ),
    }
}

function capitalizeFirstLetter(
    str: "ascending" | "descending",
): "Ascending" | "Descending" {
    return (str.charAt(0).toUpperCase() + str.slice(1)) as
        | "Ascending"
        | "Descending"
}

export const getCollectionQuery = async (
    itemId: number,
    fieldId: string,
    odataType: string,
): Promise<VisibleFieldResult> => {
    return dataAPI
        .getJSON<ODataGetResourceResponse<VisibleFieldResultDTO>>(
            `ItemFields/ItemId=${itemId},FieldDefinitionId='${fieldId}'/${odataType}/Query`,
        )
        .then((response) => {
            return {
                selectedSorting: response.Sorting?.map((option) => {
                    return {
                        mode: (option.Direction === "Descending"
                            ? "descending"
                            : "ascending") as "ascending" | "descending",
                        columnId: option.FieldDefinitionId,
                        label: option?.FieldDefinition?.Label || "",
                    }
                }),
                fields: response.Fields.map(parseVisibleFieldData),
                filters: parseFromDTO(response.Filter),
            }
        })
        .catch(async (error) => {
            if (error instanceof Response) {
                const errorMessage = formatString(
                    AnErrorOccurredWhileFetchingVisibleFieldsFor,
                    fieldId,
                )
                throw await RequestErrorDataApi.create(
                    errorMessage,
                    error.status,
                    error,
                )
            }
            throw error
        })
}

export const updateCollectionQuery = async (
    itemId: number,
    fieldId: string,
    odataType: string,
    data: {
        selectedFields?: string[]
        selectedSorting?: SortingOption[]
        filters?: Filters
    },
): Promise<VisibleFieldResult> => {
    const { selectedFields, selectedSorting, filters } = data

    const parameters: Partial<VisibleFieldResultDTO> = {}

    if (selectedFields) {
        parameters.Fields = selectedFields.map((field) => {
            return {
                Id: field,
            }
        })
    }

    if (selectedSorting) {
        parameters.Sorting = selectedSorting.map((option): SortingDTO => {
            return {
                Direction: capitalizeFirstLetter(option.mode),
                FieldDefinitionId: option.columnId,
            }
        })
    }

    if (filters) {
        parameters.Filter = parseToDTO(filters)
    }
    return dataAPI
        .patchJSON<ODataGetResourceResponse<VisibleFieldResultDTO>>(
            `ItemFields/ItemId=${itemId},FieldDefinitionId='${fieldId}'/${odataType}/Query`,
            parameters,
        )
        .then((response) => {
            return {
                selectedSorting: response.Sorting?.map((option) => {
                    return {
                        mode: (option.Direction === "Descending"
                            ? "descending"
                            : "ascending") as "ascending" | "descending",
                        columnId: option.FieldDefinitionId,
                        label: option?.FieldDefinition?.Label || "",
                    }
                }),
                fields: response.Fields.map(parseVisibleFieldData),
                filters: parseFromDTO(response.Filter),
            }
        })
        .catch(async (error) => {
            if (error instanceof Response) {
                const errorMessage = formatString(
                    AnErrorOccurredWhileFetchingVisibleFieldsFor,
                    fieldId,
                )
                throw await RequestErrorDataApi.create(
                    errorMessage,
                    error.status,
                    error,
                )
            }
            throw error
        })
}

const parseConceptFieldData = (
    fields: ConceptFieldGroupDTO[],
    groupData: GroupData[],
): TreeNodeData<ConceptFieldTree>[] => {
    const groupedConcepts = new Map<string, TreeNodeData<ConceptFieldTree>>()
    let topLevelConcepts: TreeNodeData<ConceptFieldTree>[] = []

    const groupTotalMap = new Map<string, number>()
    groupData.forEach((dataItem) => {
        if (dataItem.id) {
            groupTotalMap.set(dataItem.id, dataItem.total)
        }
    })

    fields.forEach((item) => {
        const conceptField: TreeNodeData<ConceptFieldTree> = {
            id: item.Id,
            label: item.Label,
            data: { id: item.Id, label: item.Label },
            isSelected: false,
            isSelectable: true,
            hasChildren: false,
        }

        if (item.Group && item.Group.Id) {
            const groupId = item.Group.Id
            let groupConcept = groupedConcepts.get(groupId)

            if (!groupConcept) {
                // Create new group if it doesn't exist
                groupConcept = {
                    id: groupId,
                    label: item.Group.Label,
                    data: { id: groupId, label: item.Group.Label },
                    isSelected: false,
                    isSelectable: false,
                    isExpanded: true,
                    hasChildren: true,
                    children: [],
                    childrenCount: groupTotalMap.get(groupId),
                }
                groupedConcepts.set(groupId, groupConcept)
                topLevelConcepts.push(groupConcept) // Add group as a top-level item
            }

            groupConcept.children?.push(conceptField) // Add the current item as a child of the group
        } else {
            // Item has no group, add it as a top-level concept
            conceptField.hasChildren = false
            conceptField.isSelectable = true // Make non-grouped items selectable
            topLevelConcepts.push(conceptField)
        }
    })

    // all non group elements should be at the bottom on the list
    const noGroupConcepts: TreeNodeData<ConceptFieldTree>[] = []
    topLevelConcepts = topLevelConcepts.filter((concept) => {
        if (
            !concept.hasChildren ||
            (concept.children && concept.children.length === 0)
        ) {
            noGroupConcepts.push(concept)
            return false
        }
        return true
    })

    topLevelConcepts = topLevelConcepts.concat(noGroupConcepts)

    return topLevelConcepts
}

export const getAllConceptFields = async (
    itemId: number,
    fieldId: string,
    odataType: string,
    filter = "",
    skip = 0,
    top = 20,
    groupData: GroupData[],
): Promise<ConceptFieldsResult<ConceptFieldTree>> => {
    const parameters = {
        $filter: `contains(Label, '${filter}')`,
        $orderby: "Group/IsFactoryDefault desc, Group/Label asc, Label asc",
        expand: "group",
        $count: true,
        $top: top,
        $skip: skip,
    }

    return dataAPI
        .getJSON<ODataGetArrayResponse<ConceptFieldGroupDTO>>(
            `ItemFields(ItemId=${itemId},FieldDefinitionId='${fieldId}')/${odataType}/Query/SelectableFields`,
            parameters,
        )
        .then((response) => {
            return {
                recordsCount: response.value.length,
                fields: parseConceptFieldData(response.value, groupData),
                count: response["@count"] ?? 0,
            }
        })
        .catch(async (error) => {
            if (error instanceof Response) {
                const errorMessage = formatString(
                    AnErrorOccurredWhileFetchingAllConceptFields,
                )
                throw await RequestErrorDataApi.create(
                    errorMessage,
                    error.status,
                    error,
                )
            }
            throw error
        })
}

export const getConceptGroups = async (): Promise<GroupData[]> => {
    return dataAPI
        .getJSON<ODataGetArrayResponse<ConceptFieldGroupDTO>>(
            "FieldDefinitions?$apply=groupby((Group/Label,Group/id),aggregate($count%20as%20Total))",
        )
        .then((response) => {
            return response.value.map((group) => {
                return { total: group?.Total ?? 0, id: group?.Group?.Id ?? "" }
            })
        })
        .catch(async (error) => {
            if (error instanceof Response) {
                const errorMessage = formatString(
                    AnErrorOccurredWhileFetchingConceptFieldGroups,
                )
                throw await RequestErrorDataApi.create(
                    errorMessage,
                    error.status,
                    error,
                )
            }
            throw error
        })
}

export const getCollectionItems = async (
    itemId: number,
    fieldId: string,
    odataType: string,
    userFormattingSettings: IUserFormattingSettings,
    skip = 0,
    top = 20,
    ids: number[] | null = null,
): Promise<IItemsCollectionResult> => {
    if (ids?.length === 0) {
        return {
            items: [],
            count: 0,
        }
    }
    const idsFilter = !!ids
        ? `&$filter=Item/Id in (${ids.join(",")})&$expand=Item`
        : ""
    return dataAPI
        .getJSON<ODataGetArrayResponse<CollectionItemViewDTO>>(
            `ItemFields/ItemId=${itemId},FieldDefinitionId='${fieldId}'/${odataType}/Query/View()?$top=${top}&$skip=${skip}&$count=true${idsFilter}`,
        )
        .then((response) => {
            const mappedItems = response.value.map((itemView) => {
                const mappedFields = itemView.Components.map((component) => {
                    return {
                        ...parseFieldData(component, userFormattingSettings),
                        description: "",
                        title: "",
                    } as IField
                })
                const fields = mappedFields.reduce(
                    (previousValue, currentValue) => {
                        return {
                            ...previousValue,
                            [currentValue.fieldId]: currentValue,
                        }
                    },
                    {} as Record<string, IField>,
                )
                return {
                    id: itemView.ItemId,
                    number: (fields?.[NumberCode]?.value as string) ?? "",
                    title: (fields?.[TitleCode]?.value as string) ?? "",
                    fields: fields,
                } as IItem
            })

            return {
                items: mappedItems,
                count: response["@count"],
            } as IItemsCollectionResult
        })
        .catch(async (error) => {
            if (error instanceof Response) {
                const errorMessage = formatString(
                    AnErrorOccurredWhileFetchingVisibleFieldsFor,
                    fieldId,
                )
                throw await RequestErrorDataApi.create(
                    errorMessage,
                    error.status,
                    error,
                )
            }
            throw error
        })
}

export const getAvailableSortingFields = async (
    itemId: number,
    fieldId: string,
    odataType: string,
    filter = "",
    skip = 0,
    top = 20,
    localSelectedId: string[] = [],
): Promise<ConceptFieldsResult<SortingOptionTree>> => {
    try {
        const groupsResponse = await dataAPI.getJSON<
            ODataGetArrayResponse<ConceptFieldGroupDTO>
        >(
            "FieldDefinitions?$apply=groupby((Group/Label,Group/id),aggregate($count%20as%20Total))",
        )

        const groupData: GroupData[] = groupsResponse.value.map((group) => {
            return { total: group?.Total ?? 0, id: group?.Group?.Id ?? "" }
        })

        let filterQuery = `contains(Label, '${filter}')`
        if (localSelectedId && localSelectedId.length > 0) {
            localSelectedId.forEach((id) => {
                filterQuery += ` and not(Id eq '${id}')`
            })
        }

        const parameters = {
            $filter: filterQuery,
            $orderby: "Group/IsFactoryDefault desc, Group/Label asc, Label asc",
            $expand: "group",
            $count: true,
            $top: top,
            $skip: skip,
        }

        const fieldsResponse = await dataAPI.getJSON<
            ODataGetArrayResponse<ConceptFieldGroupDTO>
        >(
            `ItemFields(ItemId=${itemId},FieldDefinitionId='${fieldId}')/${odataType}/Query/SelectableFields`,
            parameters,
        )

        return {
            recordsCount: fieldsResponse.value.length,
            fields: parseConceptFieldData(fieldsResponse.value, groupData),
            count: fieldsResponse["@count"] ?? 0,
        }
    } catch (error) {
        if (error instanceof Response) {
            const errorMessage = formatString(
                AnErrorOccurredWhileFetchingVisibleFieldsFor,
                fieldId,
            )
            throw await RequestErrorDataApi.create(
                errorMessage,
                error.status,
                error,
            )
        }
        throw error
    }
}
